import {HttpClient} from '@angular/common/http';
import {Inject, Injectable} from '@angular/core';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {REMIT_BASE_URL} from './config';
import {PartnerRole} from './remit-agency-partner.service';
import {PaginationParams} from './remit-invoice.service';

/**
 * Commission status enum
 */
export type CommissionStatus = 'accrued' | 'settled' | 'reversed';

/**
 * Commission program enum
 */
export type Program = 'agency-referral';

/**
 * Commission settlement state
 */
export type CommissionSettlementState =
  | 'draft'
  | 'approved'
  | 'executed'
  | 'rejected';

/**
 * Commission query parameters
 * Subset of backend remitinvoice.CommissionQueryParams
 */
export interface CommissionQueryParams extends PaginationParams {
  partnerId?: string;
  statuses?: CommissionStatus[];
  createdAtStart?: string;
  createdAtEnd?: string;
  sortBy?: string;
  sortDirection?: number; // 1 for asc, -1 for desc
  invoiceId?: string;
  invoiceIds?: string[];
  ids?: string[];
  userId?: string;
  partnerRole?: PartnerRole;
  program?: Program;
  settlementBatchId?: string;
}

/**
 * Single commission record
 */
export interface Commission {
  id: string;
  invoiceId: string;
  userId: string;
  partnerId: string;
  partnerRole: PartnerRole;
  parentPartnerId?: string;
  agencyCode: string;
  amountTWD: string;
  currency: string;
  status: CommissionStatus;
  program: Program;
  superCommissionPerTxnTWD?: string;
  agentCommissionPerTxnTWD?: string;
  paidAt?: string;
  paidBy?: string;
  settlementBatchId?: string;
  pendingBatchId?: string;
  note?: string;
  createdAt: string;
  createdBy: string;
  updatedAt?: string;
  updatedBy?: string;
  version?: string;
}

/**
 * Commission query response
 */
export interface CommissionQueryResponse {
  message: string;
  body: Commission[];
  totalData: number;
}

/**
 * Commission settlement batch record
 */
export interface CommissionSettlement {
  id: string;
  state: CommissionSettlementState;
  createdAt: string;
  createdBy: string;
  paidAt: string;
  paidBy: string;
  approvedAt?: string;
  approvedBy?: string;
  executedAt?: string;
  executedBy?: string;
  ledgerEntryId?: string;
  attachmentUrl?: string;
  filters: CommissionQueryParams;
  commissionIds: string[];
  count: number;
  totalAmount: string;
  note?: string;
  version?: string;
}

/**
 * Request payload for marking commissions as paid
 */
export interface MarkPaidRequest {
  partnerId?: string;
  partnerRole?: PartnerRole;
  invoiceId?: string;
  invoiceIds?: string[];
  ids?: string[];
  statuses?: CommissionStatus[];
  createdAtStart?: string;
  createdAtEnd?: string;
  program?: Program;
  paidAt?: string;
  note?: string;
}

/**
 * Response payload for mark-paid action
 */
export interface MarkPaidResponse {
  message: string;
  updatedCount: number;
  skippedCount: number;
  batchId: string;
  errors: {id: string; reason: string}[];
}

/**
 * Request payload for proposing a commission settlement batch
 */
export interface CommissionBatchProposeRequest
  extends Partial<CommissionQueryParams> {
  note?: string;
}

/**
 * Response payload for proposing a commission settlement batch
 */
export interface CommissionBatchProposeResponse {
  message: string;
  batch: CommissionSettlement;
  totalData: number;
}

/**
 * Request payload for approving a commission settlement batch
 */
export interface CommissionBatchApproveRequest {
  batchId: string;
  note?: string;
}

/**
 * Request payload for executing a commission settlement batch
 */
export interface CommissionBatchExecuteRequest {
  batchId: string;
  ledgerEntryId?: string;
  attachmentUrl?: string;
  paidAt?: string;
}

/**
 * Response payload for settlement batch state transitions
 */
export interface CommissionBatchMutationResponse {
  message: string;
  batch: CommissionSettlement;
}

/**
 * Query parameters for listing settlement batches
 */
export interface CommissionSettlementQueryParams extends PaginationParams {
  ids?: string[];
  state?: CommissionSettlementState;
  createdBy?: string;
  approvedBy?: string;
  executedBy?: string;
  createdAtStart?: string;
  createdAtEnd?: string;
  sortBy?: string;
  sortDirection?: number;
}

/**
 * Response payload for listing settlement batches
 */
export interface CommissionSettlementListResponse {
  message: string;
  body: CommissionSettlement[];
  totalData: number;
}

/**
 * Response payload for fetching a single settlement batch
 */
export interface CommissionSettlementResponse {
  message: string;
  batch: CommissionSettlement;
}

/**
 * Request payload for reversing/undoing commissions
 */
export interface ReverseRequest {
  commissionIds: string[];
  targetStatus?: 'reversed' | 'accrued';
}

/**
 * Response payload for reverse/undo action
 */
export interface ReverseResponse {
  message: string;
  updatedCount: number;
  skippedCount: number;
  errors: {id: string; reason: string}[];
}

/**
 * Service for querying commission data
 */
@Injectable({
  providedIn: 'root',
})
export class RemitCommissionService {
  constructor(
    private http: HttpClient,
    @Inject(REMIT_BASE_URL) private remitApiUrl: string,
  ) {}

  /**
   * Query commissions with filtering, sorting, and pagination
   * @param {CommissionQueryParams} params - Query parameters
   * @return {Observable<CommissionQueryResponse>} Observable of commission
   * query response
   */
  queryCommissions(
      params: CommissionQueryParams,
  ): Observable<CommissionQueryResponse> {
    return this.http
        .post<CommissionQueryResponse>(
            this.remitApiUrl + '/staff/commissions',
            params,
        )
        .pipe(
            map((response) => {
              // Standardize "data is empty" response to empty array
              if (
                response.message === 'data is empty' ||
              !response.body ||
              response.body.length === 0
              ) {
                return {
                  message: response.message,
                  body: [],
                  totalData: 0,
                };
              }
              return response;
            }),
        );
  }

  /**
   * Mark commissions as paid (settle) using filters
   * @param {MarkPaidRequest} params - Filters and metadata for settlement
   * @return {Observable<MarkPaidResponse>} Settlement result
   */
  markPaid(params: MarkPaidRequest): Observable<MarkPaidResponse> {
    const hasFilter =
      params.partnerId ||
      params.program ||
      params.invoiceId ||
      (params.invoiceIds && params.invoiceIds.length > 0) ||
      (params.ids && params.ids.length > 0);

    if (!hasFilter) {
      throw new Error('markPaid requires at least one filter to prevent broad updates');
    }

    const payload: MarkPaidRequest & PaginationParams = {
      ...params,
      statuses: params.statuses && params.statuses.
          length > 0 ? params.statuses : ['accrued'],
      pageNumber: 0,
      pageSize: -1,
    };

    return this.http
        .post<MarkPaidResponse>(
            `${this.remitApiUrl}/staff/commissions/mark-paid`,
            payload,
        )
        .pipe(
            map((response) => ({
              ...response,
              errors: response.errors || [],
            })),
        );
  }

  /**
   * Propose a new settlement batch (draft) using filters
   * @param {CommissionBatchProposeRequest} params - Filters and note for proposal
   * @return {Observable<CommissionBatchProposeResponse>} Proposal result
   */
  proposeSettlementBatch(
      params: CommissionBatchProposeRequest,
  ): Observable<CommissionBatchProposeResponse> {
    const hasFilter =
      params.partnerId ||
      params.program ||
      params.invoiceId ||
      (params.invoiceIds && params.invoiceIds.length > 0) ||
      (params.ids && params.ids.length > 0);

    if (!hasFilter) {
      throw new Error('proposeSettlementBatch requires at least one filter');
    }

    const payload: CommissionBatchProposeRequest & PaginationParams = {
      ...params,
      statuses: params.statuses && params.statuses.length > 0 ?
        params.statuses :
        ['accrued'],
      pageNumber: 0,
      pageSize: -1,
    };

    return this.http.post<CommissionBatchProposeResponse>(
        `${this.remitApiUrl}/staff/commissions/batches/propose`,
        payload,
    );
  }

  /**
   * Approve a draft settlement batch
   * @param {CommissionBatchApproveRequest} params - Batch ID and optional note
   * @return {Observable<CommissionBatchMutationResponse>} Approval result
   */
  approveSettlementBatch(
      params: CommissionBatchApproveRequest,
  ): Observable<CommissionBatchMutationResponse> {
    if (!params.batchId) {
      throw new Error('approveSettlementBatch requires batchId');
    }
    return this.http.post<CommissionBatchMutationResponse>(
        `${this.remitApiUrl}/staff/commissions/batches/approve`,
        params,
    );
  }

  /**
   * Execute an approved settlement batch
   * @param {CommissionBatchExecuteRequest} params - Batch ID and execution metadata
   * @return {Observable<CommissionBatchMutationResponse>} Execution result
   */
  executeSettlementBatch(
      params: CommissionBatchExecuteRequest,
  ): Observable<CommissionBatchMutationResponse> {
    if (!params.batchId) {
      throw new Error('executeSettlementBatch requires batchId');
    }
    return this.http.post<CommissionBatchMutationResponse>(
        `${this.remitApiUrl}/staff/commissions/batches/execute`,
        params,
    );
  }

  /**
   * List settlement batches with optional filtering
   * @param {CommissionSettlementQueryParams} params - Query parameters
   * @return {Observable<CommissionSettlementListResponse>} Batch list
   */
  querySettlementBatches(
      params: CommissionSettlementQueryParams,
  ): Observable<CommissionSettlementListResponse> {
    const payload: CommissionSettlementQueryParams = {
      pageNumber: params.pageNumber ?? 0,
      pageSize: params.pageSize ?? 10,
      ids: params.ids,
      state: params.state,
      createdBy: params.createdBy,
      approvedBy: params.approvedBy,
      executedBy: params.executedBy,
      createdAtStart: params.createdAtStart,
      createdAtEnd: params.createdAtEnd,
      sortBy: params.sortBy ?? 'createdAt',
      sortDirection: params.sortDirection ?? -1,
    };

    return this.http.post<CommissionSettlementListResponse>(
        `${this.remitApiUrl}/staff/commissions/batches`,
        payload,
    ).pipe(
        map((response) => {
          if (
            response.message === 'data is empty' ||
          !response.body ||
          response.body.length === 0
          ) {
            return {
              message: response.message,
              body: [],
              totalData: 0,
            };
          }
          return response;
        }),
    );
  }

  /**
   * Fetch a single settlement batch by ID
   * @param {string} batchId - Settlement batch ID
   * @return {Observable<CommissionSettlement>} Settlement batch detail
   */
  getSettlementBatch(batchId: string): Observable<CommissionSettlement> {
    if (!batchId) {
      throw new Error('getSettlementBatch requires batchId');
    }
    return this.http
        .get<CommissionSettlementResponse>(
            `${this.remitApiUrl}/staff/commissions/batches/${batchId}`,
        )
        .pipe(map((response) => response.batch));
  }

  /**
   * Manually reverse or undo commissions by IDs
   * @param {ReverseRequest} request - Commission IDs and target status
   * @return {Observable<ReverseResponse>} Reverse/undo result
   */
  reverse(request: ReverseRequest): Observable<ReverseResponse> {
    if (!request.commissionIds || request.commissionIds.length === 0) {
      throw new Error('reverse requires at least one commissionId');
    }

    const payload: ReverseRequest = {
      ...request,
      targetStatus: request.targetStatus || 'reversed',
    };

    return this.http
        .post<ReverseResponse>(
            `${this.remitApiUrl}/staff/commissions/reverse`,
            payload,
        )
        .pipe(
            map((response) => ({
              ...response,
              errors: response.errors || [],
            })),
        );
  }
}
